// ---------------------------------------------------------------------------
// Display Data Hook — WebSocket + HTTP polling fallback
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from "react";
import type { DisplayWsMessage } from "@ease/shared";

const DISPLAY_SERVER_URL = import.meta.env.VITE_DISPLAY_SERVER_URL ?? "";
const POLL_INTERVAL_MS = 15_000;
const WS_RECONNECT_BASE_MS = 1_000;
const WS_RECONNECT_MAX_MS = 30_000;
const STALE_THRESHOLD_MS = 300_000;

export type DisplayConnectionStatus = "connecting" | "connected" | "polling" | "error";

interface UseDisplayDataResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  isStale: boolean;
  connectionStatus: DisplayConnectionStatus;
  lastUpdatedAt: string | null;
}

export function useDisplayData<T>(
  displayId: string,
  token: string,
  dataPath: string,
): UseDisplayDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<DisplayConnectionStatus>("connecting");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const staleCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    if (!displayId || !token) return;
    try {
      const url = `${DISPLAY_SERVER_URL}/display/${displayId}/${dataPath}`;
      const response = await fetch(url, {
        headers: { "X-Display-Token": token },
        signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) throw new Error(`${response.status}: ${response.statusText}`);
      const json = (await response.json()) as { data: T };
      setData(json.data);
      setError(null);
      setLastUpdatedAt(new Date().toISOString());
      setIsStale(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load display data");
    } finally {
      setIsLoading(false);
    }
  }, [displayId, token, dataPath]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    setConnectionStatus("polling");
    pollIntervalRef.current = setInterval(() => void fetchData(), POLL_INTERVAL_MS);
  }, [fetchData]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!displayId || !token || !DISPLAY_SERVER_URL) {
      void fetchData();
      startPolling();
      return;
    }

    let isCancelled = false;

    function connect() {
      if (isCancelled) return;
      const wsUrl = `${DISPLAY_SERVER_URL.replace(/^http/, "ws")}/ws?displayId=${encodeURIComponent(displayId)}&token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      setConnectionStatus("connecting");

      ws.onopen = () => {
        if (isCancelled) { ws.close(); return; }
        setConnectionStatus("connected");
        reconnectAttemptRef.current = 0;
        stopPolling();
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 30_000);
        ws.addEventListener("close", () => clearInterval(pingInterval), { once: true });
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(String(event.data)) as DisplayWsMessage;
          if (msg.type === "initial_data" || msg.type === "data_update") {
            setData(msg.data as T);
            setError(null);
            setIsLoading(false);
            setIsStale(false);
            setLastUpdatedAt(msg.timestamp);
          } else if (msg.type === "stale_warning") {
            setIsStale(true);
          }
        } catch {
          // Invalid message
        }
      };

      ws.onclose = () => {
        if (isCancelled) return;
        wsRef.current = null;
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(WS_RECONNECT_BASE_MS * Math.pow(2, attempt), WS_RECONNECT_MAX_MS);
        reconnectAttemptRef.current = attempt + 1;
        void fetchData();
        startPolling();
        setTimeout(connect, delay);
      };
    }

    void fetchData();
    connect();

    staleCheckRef.current = setInterval(() => {
      setLastUpdatedAt((prev) => {
        if (!prev) return prev;
        if (Date.now() - new Date(prev).getTime() > STALE_THRESHOLD_MS) setIsStale(true);
        return prev;
      });
    }, 60_000);

    return () => {
      isCancelled = true;
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      stopPolling();
      if (staleCheckRef.current) clearInterval(staleCheckRef.current);
    };
  }, [displayId, token, fetchData, startPolling, stopPolling]);

  return { data, isLoading, error, isStale, connectionStatus, lastUpdatedAt };
}

export function useDisplayMutation<TInput, TResult>(
  displayId: string,
  token: string,
  path: string,
  method: "POST" = "POST",
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (input?: TInput): Promise<TResult | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `${DISPLAY_SERVER_URL}/display/${displayId}/${path}`;
      const response = await fetch(url, {
        method,
        headers: { "X-Display-Token": token, "Content-Type": "application/json" },
        body: input ? JSON.stringify(input) : undefined,
        signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({ error: "Request failed" }))) as { error?: string };
        throw new Error(body.error ?? "Request failed");
      }
      return ((await response.json()) as { data: TResult }).data;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [displayId, token, path, method]);

  return { mutate, isLoading, error };
}
